import React from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import useStartOperator from 'lib/hooks/useStartOperator';

import Routes from 'lib/utils/routes';
import { now, toISO } from 'lib/utils/date';
import { DefaultLoading } from 'components/Loading';
import { UserSelect } from 'components/UserSelect';
import { getMachine } from 'lib/selectors/getMachine';
import { getOperators, getOperatorsLoading } from 'lib/selectors/getOperators';
import { getIsOperatorTrackingEnabled } from 'lib/selectors/getIsOperatorTrackingEnabled';
import { useRequestOperators } from 'lib/hooks/useRequestOperators';

import { throwErrorToast } from 'lib/utils/toast';
import { getIsLaborTicketEnabled } from 'lib/selectors/getIsLaborTicketEnabled';
import { getLoadingCurrentWorkOrderOp } from 'lib/selectors/getLoadingCurrentWorkOrderOp';
import { getShiftHandoverEnabled } from 'lib/selectors/getShiftHandoverEnabled';
import { actionOpenShiftIntro, actionCloseSelectOperator } from 'lib/actions';
import useSelectOperator from 'lib/hooks/useSelectOperator';

export const SelectOperator = () => {
  const dispatch = useDispatch();
  const operatorsLoading = useSelector(getOperatorsLoading);
  const operators = useSelector(getOperators);
  const isOperatorTrackingEnabled = useSelector(getIsOperatorTrackingEnabled);
  const loadingCurrentWorkOrderOp = useSelector(getLoadingCurrentWorkOrderOp);

  const startOperator = useStartOperator();

  const machine = useSelector(getMachine);
  const { t } = useTranslation();

  const laborTicketEnabled = useSelector(getIsLaborTicketEnabled);
  const shiftHandoverEnabled = useSelector(getShiftHandoverEnabled);

  const {
    selectOperatorOpen,
    selectOperatorOptions,
    closeSelectOperator,
  } = useSelectOperator();

  // Only fetch operators while the sign-in modal is open. Apollo runs the
  // query when skip flips to false, so no-cache refetches fresh on each open
  // without a separate request on app load (useInitialData seeds redux).
  const { loading: operatorsQueryLoading } = useRequestOperators({
    skip: !isOperatorTrackingEnabled || !selectOperatorOpen,
  });

  const loading =
    operatorsLoading ||
    (selectOperatorOpen && operatorsQueryLoading && operators.length === 0);

  const redirectPath =
    selectOperatorOptions?.redirectPath ||
    (machine?.id && Routes.machineIdHomePath(machine.id));

  if (
    (loading || (laborTicketEnabled && loadingCurrentWorkOrderOp)) &&
    selectOperatorOpen
  ) {
    return <DefaultLoading />;
  }

  const onSubmit = async (operator, opts = {}) => {
    try {
      const mutationResult = await startOperator({
        machineId: machine.id,
        operatorId: operator.id,
        pinToPreviousEvent: opts.pinToPreviousEvent,
        startAt: opts.startAt || toISO(now().valueOf()),
        // enforce startAtOverride if provided
        startAtOverride: opts.startAtOverride,
      });

      if (!mutationResult) return null;

      const { data, errors } = mutationResult;

      if (errors) {
        throwErrorToast(t('Something went wrong'), {
          autoClose: 1500,
        });
        return null;
      }

      // Open shift intro for embedded labor flow users (don't redirect)
      // Only show intro if this signin created a new timesheet and shift handover is enabled
      if (shiftHandoverEnabled) {
        dispatch(actionCloseSelectOperator());

        const timesheetCreated =
          data?.startOperatorResponse?.timesheet?.created ||
          data?.submitLaborTicketTransactionResponse?.startOperatorRun
            ?.timesheet?.created;

        if (timesheetCreated) {
          dispatch(actionOpenShiftIntro());
          return true;
        }

        closeSelectOperator(redirectPath);
        return true;
      }

      closeSelectOperator(redirectPath);
      return true;
    } catch (e) {
      if (e?.networkError) {
        if (e?.networkError?.result?.errors?.length > 0) {
          e.networkError.result.errors.forEach(({ message }) => {
            throwErrorToast(message, {
              autoClose: 1500,
            });
          });
          return null;
        }
        if (e?.networkError?.result?.message) {
          throwErrorToast(e.networkError.result.message, {
            autoClose: 1500,
          });
          return null;
        }
      }
    }
    return null;
  };

  return (
    <UserSelect
      onSubmit={onSubmit}
      isOpen={selectOperatorOpen}
      onClose={() => {
        return closeSelectOperator(Routes.machineIdHomePath(machine.id));
      }}
    />
  );
};
